import type { CaptionListenerAction, CaptionListenerApi } from './types';

import {
  selectShouldRunCaptionsSession,
  setCaptionRequest,
  startCaptionsSessionThunk,
  stopCaptionsSessionThunk,
} from 'features/call/call-captions/store';
import {
  connectCall,
  selectIsConnected,
} from 'features/call/call-ui-state/store';
import { TeamingStatus } from 'features/teaming/teaming-base/enums';
import { teamingStatusSelector } from 'features/teaming/teaming-base/store';
import {
  setVoiceSession,
  setVoiceSessionId,
  voiceSessionIdSelector,
} from 'features/voice-session/store';

// connectCall → call-connected
// setVoiceSession → voice-session-ready
// setVoiceSessionId is the reconnect path. The session matcher guarantees it.
export const getCaptionStartSource = (action: CaptionListenerAction) => {
  if (connectCall.match(action)) {
    return 'call-connected';
  }

  if (setVoiceSession.match(action)) {
    return 'voice-session-ready';
  }

  return 'voice-session-reconnect';
};

// setVoiceSession carries the next id on the action. setVoiceSessionId is read
// from state after the reducer. An empty next id is not a start.
const didVoiceSessionIdChange = (
  action: CaptionListenerAction,
  api: CaptionListenerApi
) => {
  const previousId = api.getOriginalState().voiceSession.sessionId;
  const nextId = setVoiceSession.match(action)
    ? action.payload.sessionId
    : api.getState().voiceSession.sessionId;

  return Boolean(nextId && nextId !== previousId);
};

export const isUnchangedVoiceSessionUpdate = (
  action: CaptionListenerAction,
  api: CaptionListenerApi
) => {
  const isVoiceSessionUpdate =
    setVoiceSessionId.match(action) || setVoiceSession.match(action);

  if (!isVoiceSessionUpdate) {
    return false;
  }

  return !didVoiceSessionIdChange(action, api);
};

// Ready means a requester still wants captions, the call is connected, and a
// voice session id exists. The thunk no-ops if this client is not the owner.
export const startCaptionsSessionIfReady = async (
  api: CaptionListenerApi,
  source: string
) => {
  const state = api.getState();

  if (
    !selectShouldRunCaptionsSession(state) ||
    !selectIsConnected(state) ||
    !voiceSessionIdSelector(state)
  ) {
    return;
  }

  await api.dispatch(startCaptionsSessionThunk({ source }));
};

// Start if any requester still wants captions; otherwise stop.
// Stop no-ops when this client is not the session owner.
export const reconcileCaptionSession = async (
  api: CaptionListenerApi,
  source: string
) => {
  if (selectShouldRunCaptionsSession(api.getState())) {
    await startCaptionsSessionIfReady(api, source);
    return;
  }

  await api.dispatch(stopCaptionsSessionThunk({ source }));
};

// After teaming ends there is no peer. Drop remote demand before reconcile so
// a departed VI cannot keep the shared ASR session running.
export const clearRemoteRequestIfTeamingEnded = (api: CaptionListenerApi) => {
  if (teamingStatusSelector(api.getState()) !== TeamingStatus.NONE) {
    return;
  }

  api.dispatch(
    setCaptionRequest({ requester: 'remoteInterpreter', enabled: false })
  );
};
